// The floor and core the floor model found (the service's polygons, in drawing units) as the entities the canvas keeps.
import { _polylineArea } from './utils.js'
import { tessellatePolyline } from './bulge.js'

export const MIN_CORE_AREA = 20000   // 2 m2 in cm2: a smaller island is not a core

const scale = (ring, s) => ring.map(([x, y]) => [x * s, y * s])

// scale: canvas units per drawing unit (appliedScale); ts: stamps the handles, so one detection's entities are recognisable.
export function floorEntities(result, s, ts) {
  const floor = (result.floor ?? []).map((p, i) => ({
    handle: `flood-${ts}-${i}`, type: 'LWPOLYLINE', layer: 'Flood', closed: true,
    points: scale(p.exterior, s), holes: p.holes.map(h => scale(h, s)),
  }))
  const roomMode = result.refinement?.source === 'rooms'
  const cores = (result.core ?? []).map(p => ({ points: scale(p.exterior, s), holes: (p.holes ?? []).map(h => scale(h, s)) }))
    .filter(p => roomMode || _polylineArea(p.points) >= MIN_CORE_AREA)
    .map(({ points, holes }, i) => ({
      handle: `core-qline-${ts}-${i}`, type: 'LWPOLYLINE', layer: 'Core Q-Line', original_layer_name: 'Core Q-Line',
      points, ...(roomMode ? { holes } : {}), closed: true, isUserDrawn: true,
    }))
  return floor.length ? [...floor, ...cores] : []
}

// What a new detection replaces: the earlier floor and the Core Q-Lines generated with it (a Core Q-Line the user drew stays).
export const isFloorEntity = p => p.layer === 'Flood' || p.handle?.startsWith('flood-') || p.handle?.startsWith('core-qline-') || false

// What the user is told about a detection: the area and, when the agent or the vision review ran, what it said; low when it is unsure, was cut short or did not run.
export function floorNote(result) {
  const r = result.refinement ?? {}
  const m2 = result.stats?.net_area_m2
  const area = m2 != null ? `${Math.round(m2)} m²` : ''
  if ((r.source === 'agent' || r.source === 'review' || r.source === 'rooms') && r.summary) {
    return { text: `${area ? `${area} — ` : ''}${r.summary}`, low: r.confidence === 'low' || r.incomplete === true }
  }
  if (r.source === 'review') return { text: `Floor ${area} traced from the plan's walls and checked by a vision model`.replace('  ', ' '), low: r.incomplete === true }
  if (r.source === 'trace') {
    return { text: `Floor ${area} traced from the plan's walls${r.error ? ` (the review could not run: ${r.error})` : ''}`.replace('  ', ' '), low: !!r.error }
  }
  return { text: `Floor ${area} from the model and the plan's geometry${r.error ? ` (the agent could not run: ${r.error})` : ''}`.replace('  ', ' '), low: !!r.error }
}


// Snapshot the displayed drawing in source units. Generated floor/Q-lines are outputs, not model input.
export function floorInput(entities, baseEntities, cmPerUnit, revisionId) {
  if (!Number.isFinite(cmPerUnit) || cmPerUnit <= 0) throw new Error('Confirm the drawing scale before detecting the floor.')
  const known = new Set(baseEntities.map(e => e.handle))
  const present = new Set()
  const modified = Object.create(null), added = [], classes = Object.create(null)
  for (const e of entities) {
    if (isFloorEntity(e) || e.layer?.endsWith('Q-Line')) continue
    if (e.holes?.length) throw new Error('Floor input contains edited geometry with holes that is not supported yet.')
    if (!['LINE', 'CIRCLE', 'LWPOLYLINE'].includes(e.type)) throw new Error(`Unsupported floor input geometry: ${e.type}`)
    if (typeof e.handle !== 'string' || !e.handle) throw new Error('Missing entity identity in floor input.')
    const handle = known.has(e.handle) ? e.handle : `u-${encodeURIComponent(e.handle)}`
    if (classes[handle] !== undefined) throw new Error('Duplicate entity identity in floor input.')
    present.add(e.handle)
    const out = { handle, type: e.type, layer: e.layer }
    const point = p => p.map(v => v / cmPerUnit)
    if (e.type === 'LINE') { out.start = point(e.start); out.end = point(e.end) }
    if (e.type === 'CIRCLE') { out.center = point(e.center); out.radius = e.radius / cmPerUnit }
    if (e.type === 'LWPOLYLINE') {
      out.points = e.points.map(p => point(p.slice(0, 2)))
      out.closed = !!e.closed
      if (e.bulge || e.bulges) out.bulges = e.bulge || e.bulges
    }
    if (known.has(e.handle)) modified[handle] = out
    else added.push(out)
    classes[handle] = e.layer
  }
  return { geometry: { deleted: [...known].filter(h => !present.has(h)), modified, added },
    current_classes: classes, cm_per_unit: cmPerUnit, revision_id: revisionId }
}

export function floorResultIsCurrent(result, revisionId, submittedEdit, currentEdit, submittedScale, currentScale) {
  return result.revision_id === revisionId && submittedEdit === currentEdit && submittedScale === currentScale
}

// Compare geometry and classes, not editor metadata or object identity. Include output
// polygons so a detection can never replace a manual floor/core edit made meanwhile.
export function floorDrawingSnapshot(entities, cmPerUnit) {
  return JSON.stringify([cmPerUnit, (entities ?? []).map(e => [
    e.handle, e.type, e.layer, e.start, e.end, e.center, e.radius,
    e.points, e.bulge ?? e.bulges ?? [], !!e.closed, e.holes ?? [],
  ]).sort((a, b) => String(a[0]).localeCompare(String(b[0])))])
}

// New cores and the resulting floor are one edit, so undo restores both together.
export async function appendDrawnEntity(edit, entity, preview, revisionId) {
  const entry = entity.layer === 'Core Q-Line' && entity.points?.length >= 3
    ? { ...entity, type: 'LWPOLYLINE', closed: true } : entity
  const floors = edit.userPolylines.filter(p => p.layer === 'Flood')
  if (entry.layer !== 'Core Q-Line' || !entry.points || entry.points.length < 3 || !floors.length) {
    return { ...edit, userPolylines: [...edit.userPolylines, entry] }
  }
  const ring = p => ({ exterior: tessellatePolyline(p.points, p.bulge ?? p.bulges ?? [], true), holes: p.holes ?? [] })
  const cores = edit.userPolylines.filter(p => p.layer === 'Core Q-Line')
  const result = await preview({ action: 'replace_core', revision_id: revisionId,
    points: entry.points.slice(0, 2), floor: floors.map(ring), core: cores.map(ring),
    new_core: [...cores, entry].map(ring) })
  if (result.revision_id !== revisionId) throw new Error('The drawing changed. Please redraw the core.')
  return { ...edit, userPolylines: [...edit.userPolylines.filter(p => p.layer !== 'Flood'), entry,
    ...floorEntities({ floor: result.floor }, 1, revisionId)] }
}
